import {
  coerceDateTime,
  destinationLabel,
  encodeDestinationSummary,
} from '../my-journeys/myJourneysModel';
import {
  classifyLogisticsError,
  coerceDateTimeLocal,
  snowflakeId,
  toApiDateTime,
} from '../journey-accommodation/accommodationModel';

/** UI convenience suggestions only — backend transportMode is a free string. */
export const TRANSPORT_MODE_SUGGESTIONS = Object.freeze([
  { value: 'FLIGHT', label: 'Flight' },
  { value: 'TRAIN', label: 'Train' },
  { value: 'BUS', label: 'Bus' },
  { value: 'FERRY', label: 'Ferry' },
  { value: 'CAR', label: 'Car' },
  { value: 'TAXI', label: 'Taxi' },
  { value: 'WALK', label: 'Walk' },
  { value: 'OTHER', label: 'Other' },
]);

const SUGGESTED_VALUES = new Set(TRANSPORT_MODE_SUGGESTIONS.map((item) => item.value));

export function isSuggestedTransportMode(value) {
  return SUGGESTED_VALUES.has(String(value || ''));
}

export function transportModeLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '未選擇';
  const known = TRANSPORT_MODE_SUGGESTIONS.find((item) => item.value === raw);
  return known ? known.label : raw;
}

export function encodeEndpointSummary(text) {
  return encodeDestinationSummary(text);
}

export function endpointLabelFromSummary(raw) {
  return destinationLabel(raw);
}

export function normalizeTransportSummary(value) {
  return {
    transportId: snowflakeId(value?.transportId),
    transportMode: value?.transportMode || '',
    transportModeLabel: transportModeLabel(value?.transportMode),
    carrierText: value?.carrierText || '',
    // 清單若帶 endpoint summary，前端解析顯示；不新增 API 欄位
    sourceLabel: endpointLabelFromSummary(value?.sourceSummaryJson),
    destinationLabel: endpointLabelFromSummary(value?.destinationSummaryJson),
    departureAt: coerceDateTimeLocal(value?.departureAt),
    arrivalAt: coerceDateTimeLocal(value?.arrivalAt),
    departureDisplay: coerceDateTime(value?.departureAt),
    arrivalDisplay: coerceDateTime(value?.arrivalAt),
    bookingReference: value?.bookingReference || '',
  };
}

export function normalizeTransportDetail(value) {
  return {
    ...normalizeTransportSummary(value),
    journeyId: snowflakeId(value?.journeyId),
    sourceSummaryJson: value?.sourceSummaryJson || null,
    destinationSummaryJson: value?.destinationSummaryJson || null,
    sourceLabel: endpointLabelFromSummary(value?.sourceSummaryJson),
    destinationLabel: endpointLabelFromSummary(value?.destinationSummaryJson),
    note: value?.note || '',
    createdByMemberId: snowflakeId(value?.createdByMemberId),
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
  };
}

export function emptyTransportDraft() {
  // PD-TRN-01 interim：未選＝空字串／null（既有 optional contract），不得預設 OTHER 或 WALK。
  return {
    transportMode: '',
    source: '',
    destination: '',
    departureAt: '',
    arrivalAt: '',
    carrierText: '',
    bookingReference: '',
    note: '',
  };
}

export function draftFromTransportDetail(detail) {
  return {
    transportMode: detail?.transportMode || '',
    source: detail?.sourceLabel || '',
    destination: detail?.destinationLabel || '',
    departureAt: detail?.departureAt || '',
    arrivalAt: detail?.arrivalAt || '',
    carrierText: detail?.carrierText || '',
    bookingReference: detail?.bookingReference || '',
    note: detail?.note || '',
  };
}

/** Full-replace Transport write body — all mutable fields every submit. */
export function buildTransportWritePayload(draft) {
  const mode = String(draft?.transportMode || '').trim();
  return {
    sourceSummaryJson: encodeEndpointSummary(draft?.source),
    destinationSummaryJson: encodeEndpointSummary(draft?.destination),
    departureAt: toApiDateTime(draft?.departureAt),
    arrivalAt: toApiDateTime(draft?.arrivalAt),
    transportMode: mode || null,
    carrierText: String(draft?.carrierText || '').trim() || null,
    bookingReference: String(draft?.bookingReference || '').trim() || null,
    note: String(draft?.note || '').trim() || null,
  };
}

export function validateTransportDraft(draft) {
  const departure = toApiDateTime(draft?.departureAt);
  const arrival = toApiDateTime(draft?.arrivalAt);
  if (departure && arrival && arrival < departure) {
    return 'Arrival must be on or after departure.';
  }
  const mode = String(draft?.transportMode || '').trim();
  if (mode.length > 32) return 'Transport mode must be at most 32 characters.';
  return '';
}

export { classifyLogisticsError, coerceDateTimeLocal, snowflakeId, toApiDateTime };
